import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TruckIcon, 
  GlobeAltIcon, 
  HeartIcon, 
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  MapPinIcon,
  BoltIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

function About() {
  const navigate = useNavigate();

  const stats = [
    { number: "500K+", label: "Successful Deliveries", icon: TruckIcon },
    { number: "50+", label: "Cities Across SA", icon: MapPinIcon },
    { number: "99.5%", label: "On-Time Rate", icon: ClockIcon },
    { number: "24/7", label: "Customer Support", icon: ShieldCheckIcon }
  ];

  const values = [
    {
      icon: ShieldCheckIcon,
      title: "Reliability",
      description: "99.5% on-time delivery rate with consistent service excellence across South Africa",
      color: "bg-blue-600"
    },
    {
      icon: BoltIcon,
      title: "Speed",
      description: "Fast delivery solutions from same-day to standard shipping across all 9 provinces",
      color: "bg-orange-500"
    },
    {
      icon: HeartIcon,
      title: "Customer Focus",
      description: "24/7 support with personalized service experiences tailored for South African businesses",
      color: "bg-pink-600"
    },
    {
      icon: ChartBarIcon,
      title: "Transparency",
      description: "Clear pricing in South African Rand with no hidden fees or surprises",
      color: "bg-green-600"
    }
  ];

  const services = [
    {
      icon: GlobeAltIcon,
      title: "International Trade",
      description: "Seamless cross-border logistics to Africa and beyond",
      features: ["Customs clearance handled", "Duty & tax calculation", "190+ countries"],
      color: "bg-orange-500"
    },
    {
      icon: BuildingOfficeIcon,
      title: "Business Solutions",
      description: "Enterprise-grade logistics for growing South African businesses",
      features: ["Volume discounts", "Dedicated account manager", "Custom reporting"],
      color: "bg-purple-600"
    },
    {
      icon: ChartBarIcon,
      title: "Supply Chain Management",
      description: "End-to-end logistics solutions for complex supply chains",
      features: ["Full supply chain optimization", "Advanced analytics", "SLA guarantees"],
      color: "bg-green-600"
    }
  ];

  const testimonials = [
    {
      text: "CargoHaul has transformed our business operations. Their reliability and understanding of South African logistics challenges is unmatched.",
      author: "Thabo Mthembu",
      role: "CEO, JHB Retail",
      location: "Johannesburg",
      rating: 5
    },
    {
      text: "The real-time tracking and customer service are exceptional. They've helped us expand our reach across South Africa with reliable delivery.",
      author: "Sarah Van der Merwe",
      role: "Operations Manager, Cape Town Imports",
      location: "Cape Town",
      rating: 5
    }
  ];

  const milestones = [
    { year: "2020", title: "Founded", description: "Started operations in Johannesburg" },
    { year: "2021", title: "Expansion", description: "Expanded to Cape Town and Durban" },
    { year: "2022", title: "Growth", description: "Reached 50+ cities across SA" },
    { year: "2024", title: "Today", description: "500K+ successful deliveries" }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
          {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-blue-700">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center mix-blend-overlay opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                About Fast Global
                <br />
                <span className="text-orange-400">Logistics</span>
              </h1>
              <p className="text-xl sm:text-2xl text-white/90 mb-8 leading-relaxed">
                Your trusted logistics partner across South Africa. We're committed to delivering 
                excellence with speed, reliability, and local expertise that understands the unique 
                challenges of South African shipping.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleNavigation('/new-shipment')}
                  className="px-8 py-4 bg-orange-500 text-white rounded-xl font-bold text-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl"
                >
                  Get Started
                </button>
                <button
                  onClick={() => handleNavigation('/contact')}
                  className="px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
                >
                  Contact Us
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="CargoHaul"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gray-900/60"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <TruckIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">Operational Excellence</p>
                        <p className="text-sm text-gray-600">Serving customers worldwide since 2020</p>
                </div>
              </div>
                    <p className="text-gray-700">
                      With facilities in major cities worldwide, we ensure your 
                      packages are handled with care and delivered efficiently across the globe.
                    </p>
              </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Stats Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-xl mb-3">
                  <stat.icon className="w-6 h-6 text-white" />
              </div>
                <div className="text-4xl font-black text-white mb-1">{stat.number}</div>
                <div className="text-gray-400 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

          {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <TruckIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-black text-gray-900">Our Mission</h2>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">
                To be South Africa's most trusted logistics partner by delivering unparalleled 
                courier services that connect businesses and individuals across the country. 
                We're committed to being your reliable partner in every delivery, ensuring every 
                package tells a story of reliability and care.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                    <GlobeAltIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-black text-gray-900">Our Vision</h2>
              </div>
              <p className="text-gray-700 leading-relaxed text-lg">
                To become South Africa's leading logistics provider, where technology meets 
                human excellence. We envision a future where distance is no barrier to connection, 
                and every delivery contributes to a more connected South Africa, supporting 
                local businesses and communities.
              </p>
            </div>
          </div>
        </div>
      </section>

          {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The principles that drive everything we do, every day
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index} 
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all"
              >
                <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <value.icon className="w-8 h-8 text-white" />
                    </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive logistics solutions tailored for South African businesses
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all"
              >
                    <div className="flex items-start gap-4 mb-6">
                  <div className={`w-16 h-16 ${service.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <service.icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                      </div>
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                        </div>
                      ))}
                    </div>
                  </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Growing from a local startup to a trusted logistics partner across South Africa
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:shadow-xl transition-all text-center"
              >
                <div className="text-4xl font-black text-blue-600 mb-2">{milestone.year}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                <p className="text-gray-600">{milestone.description}</p>
                </div>
              ))}
            </div>
          </div>
      </section>

          {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real feedback from South African businesses
            </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed text-lg">
                    "{testimonial.text}"
                  </blockquote>
                <div className="pt-6 border-t border-gray-200">
                  <div className="font-bold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
      </section>

          {/* CTA Section */}
      <section className="py-24 bg-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join thousands of satisfied customers across South Africa who trust CargoHaul 
            with their most important deliveries.
              </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => handleNavigation('/new-shipment')}
              className="px-10 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all transform hover:scale-105 shadow-xl"
                >
                  Get Started Today
                </button>
                <button
                  onClick={() => handleNavigation('/contact')}
              className="px-10 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
                >
                  Contact Sales
                </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
